import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';

// Before App.css: the same order the rules had when they lived in App.css
import './components/deal/DealTile.css';
import './App.css';
import './seo.css';
import store from './store';
import Navbar from './components/layouts/Navbar';
import Footer from './components/layouts/Footer';
import Product from './components/product/Product';
import DealGrid from './components/deal/DealGrid';
import CategoryLanding from './components/deal/CategoryLanding';
// Not lazy: the server pre-renders its hero, and a Suspense fallback would replace it
import PrimeDayPage from './components/deal/PrimeDayPage';
// Not lazy either: the server pre-renders the whole page (lib/sellersPage.js)
import SellersPage from './components/sellers/SellersPage';
import ErrorBoundary from './components/common/ErrorBoundary';
import NotFound from './components/common/NotFound';
import LoadingSpinner from './components/common/LoadingSpinner';
import Log from './utils/Log';

// Pages most visitors never open (blog with its markdown renderer, admin tools)
// are split out so the homepage and deal pages download less JavaScript.
const TnC = lazy(() => import('./components/layouts/TnC'));
const Login = lazy(() => import('./components/auth/Login'));
const GeneratePost = lazy(() => import('./components/GeneratePost/GeneratePost'));
const BlogIndex = lazy(() => import('./components/blog/BlogIndex'));
const BlogPost = lazy(() => import('./components/blog/BlogPost'));
const Unsubscribe = lazy(() => import('./components/common/Unsubscribe'));

// JWT token handling can be moved to a custom hook or context
// if(localStorage.jwtToken) {
//   setAuthToken(localStorage.jwtToken);
//   const decoded = jwt_decode(localStorage.jwtToken);
//   store.dispatch(setCurrentUser(decoded));
//   //Check for expired token
//   const currentTime = Date.now() / 1000;
//   if(decoded.exp < currentTime) {
//     store.dispatch(logoutUser());
//     window.location.href = '/login';
//   }
// }

const App = () => {
  Log.info('Console log works');

  return (
    <Provider store={store}>
      <HelmetProvider>
        <Router>
          <ErrorBoundary>
            <div className="App">
              <Navbar />
              {/* min-height keeps the footer below the fold while a page loads,
                  so it doesn't jump down when content arrives (layout shift) */}
              <main className="app-main">
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/" element={
                      <div className="container">
                        <DealGrid />
                      </div>
                    } />
                    <Route path="/prime-big-deal-days" element={<PrimeDayPage />} />
                    <Route path="/category/:slug" element={
                      <div className="container">
                        <CategoryLanding />
                      </div>
                    } />
                    <Route path="/generate" element={<GeneratePost />} />
                    <Route path="/tnc" element={<TnC />} />
                    <Route path="/login" element={
                      <div className="container">
                        <Login />
                      </div>
                    } />
                    <Route path="/deals/:dealId" element={
                      <div className="container">
                        <Product />
                      </div>
                    } />
                    <Route path="/blog" element={
                      <div className="container">
                        <BlogIndex />
                      </div>
                    } />
                    <Route path="/blog/:slug" element={
                      <div className="container">
                        <BlogPost />
                      </div>
                    } />
                    <Route path="/unsubscribe" element={
                      <div className="container">
                        <Unsubscribe />
                      </div>
                    } />
                    <Route path="/sellers" element={<SellersPage />} />
                    {/* Keep in sync with lib/pageRoutes.js, which sends 404 for anything not listed */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </main>
              <Footer />
            </div>
          </ErrorBoundary>
        </Router>
      </HelmetProvider>
    </Provider>
  );
};

export default App;
